<template>
  <section>
    <v-row>
      <v-col cols="6">
        <div class="pb-2">{{ $t("currency") }}</div>
        <v-select
          v-model="selectedCurrency"
          :items="currencies"
          item-text="name"
          item-value="id"
          :placeholder="$t('currency')"
          return-object
          outlined
          hide-details
        >
          <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-avatar size="30">
                <v-img :src="item.flag" :alt="item.name" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.code }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.store_exchange_rate | NumberFormat }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template v-slot:selection="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="30" class="mr-2">
                <v-img :src="item.flag" :alt="item.name" />
              </v-avatar>
              <span>{{ item.code }}</span>
            </div>
          </template>
        </v-select>
      </v-col>
      <v-col cols="6">
        <CurrencyInput v-model="displayExchangeRate" label="rate" />
      </v-col>
    </v-row>
    <div class="d-flex justify-content-between py-5 px-3">
      <v-btn icon @click="clear">
        <v-icon>fal fa-times-circle</v-icon>
      </v-btn>
      <div class="font-weight-bold text-h4">
        {{ inputBuffer | NumberFormat }}
      </div>
    </div>

    <div class="numeric-keyboard">
      <v-row v-for="(row, rowIndex) in keys" :key="rowIndex" class="ma-0">
        <v-col
          v-for="(key, colIndex) in row"
          :key="key && key.id ? key.id : rowIndex + '-' + colIndex"
          cols="4"
          class="pa-0"
        >
          <v-btn
            v-if="key && (key.label || key.icon)"
            tile
            :class="key.class || ''"
            @click="
              key.action
                ? key.action()
                : key.label
                ? appendToInput(key.label)
                : null
            "
          >
            <v-icon v-if="key.icon">{{ key.icon }}</v-icon>
            <span v-else-if="key.label">{{ key.label }}</span>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <div class="pt-3">
      <v-btn
        block
        height="50"
        class="submit-btn"
        color="primary"
        @click="submit"
      >
        {{ $t("ok") }}
      </v-btn>
    </div>
  </section>
</template>

<script>
import CurrencyInput from "@/components/CurrencyInput";
import { myMixin } from "@/mixin/main";
import { mainStore } from "@/stores/modules/cart";

export default {
  mixins: [myMixin],
  components: {
    CurrencyInput,
  },
  name: "NumericExchangeRate",
  props: {
    totalPrice: {
      type: Number,
      required: true,
    },
    currencies: {
      type: Array,
      required: true,
    },
    productCurrency: {
      type: Object,
      default: () => ({ code: "LAK", symbol: "₭" }),
    },
    debug: { type: Boolean, default: false },
  },
  data() {
    return {
      defineStore: mainStore(),
      selectedCurrency: null,
      windowListener: this.handleKeydown,
      displayExchangeRate: "0",
      inputBuffer: "0",
      isInitializingSelection: false,
    };
  },

  mounted() {
    const { currencySymbol, currency } = this.defineStore;

    this.windowListener = this.handleKeydown.bind(this);
    window.addEventListener("keydown", this.windowListener);
    // Initialize selected currency to the first item if not set
    this.isInitializingSelection = true;
    if (!this.selectedCurrency && currency) {
      this.dlog("initial store currency:", currency);
      this.selectedCurrency = currency;
      // prefer stored buffer if available; otherwise we'll compute a default below
      if (currency.input_buffer !== undefined) {
        this.inputBuffer = currency.input_buffer || "0";
      }
      // Use the actual product currency from the prop, not assumptions based on price
      const actualProductCurrency = this.productCurrency.code;

      // Find the correct exchange rate to use for conversion
      let exchangeRateToUse;
      let rateSource;

      if (actualProductCurrency === "LAK" && currency.code === "THB") {
        // Converting from LAK to THB: use THB's exchange rate
        exchangeRateToUse = Number(
          currency.store_exchange_rate || currency.exchange_rate || 1
        );
        rateSource = "THB rate";
      } else if (actualProductCurrency === "THB" && currency.code === "LAK") {
        // Converting from THB to LAK: use THB's exchange rate (find THB currency from list)
        const thbCurrency = this.currencies.find((c) => c.code === "THB");
        exchangeRateToUse = Number(
          thbCurrency?.store_exchange_rate || thbCurrency?.exchange_rate || 1
        );
        rateSource = "THB rate from currencies list";
      } else {
        // Same currency or fallback
        exchangeRateToUse = Number(
          currency.store_exchange_rate || currency.exchange_rate || 1
        );
        rateSource = "selected currency rate";
      }

      // Determine conversion direction based on product currency vs selected currency
      let amount;
      let formula;

      if (actualProductCurrency === currency.code) {
        // Same currency, no conversion needed
        amount = this.totalPrice;
        formula = `${this.totalPrice} (same currency)`;
      } else if (actualProductCurrency === "LAK" && currency.code === "THB") {
        // Converting from LAK to THB: divide by THB rate
        amount =
          exchangeRateToUse > 0
            ? this.totalPrice / exchangeRateToUse
            : this.totalPrice;
        formula = `${this.totalPrice} / ${exchangeRateToUse} = ${amount}`;
      } else if (actualProductCurrency === "THB" && currency.code === "LAK") {
        // Converting from THB to LAK: multiply by THB rate
        amount = this.totalPrice * exchangeRateToUse;
        formula = `${this.totalPrice} * ${exchangeRateToUse} = ${amount}`;
      } else {
        // Fallback: assume totalPrice is in LAK (old behavior)
        amount =
          exchangeRateToUse > 0
            ? this.totalPrice / exchangeRateToUse
            : this.totalPrice;
        formula = `${this.totalPrice} / ${exchangeRateToUse} = ${amount} (fallback)`;
      }

      this.displayExchangeRate = exchangeRateToUse.toFixed(2).toString();

      // if no stored buffer, align it with computed value
      if (
        currency.input_buffer === undefined ||
        currency.input_buffer === null
      ) {
        this.inputBuffer = amount.toFixed(2).toString();
      }
    } else if (
      !this.selectedCurrency &&
      this.currencies &&
      this.currencies.length
    ) {
      let find = this.currencies.find(
        (item) => item.code === currencySymbol.code
      );
      if (find) {
        this.selectedCurrency = find;
      } else {
        this.selectedCurrency = this.currencies[0];
      }
      this.dlog("initial selected from list:", this.selectedCurrency);
      // Initialize display value based on selected currency and total price
      this.updateDisplayFromRate();
    } else {
      // Fallback to ensure values are consistent
      this.updateDisplayFromRate();
    }
    this.$nextTick(() => {
      this.isInitializingSelection = false;
    });
  },

  destroyed() {
    try {
      if (this.windowListener) {
        window.removeEventListener("keydown", this.windowListener);
      }
    } catch (error) {
      console.error("Error in destroyed lifecycle:", error);
    }
  },

  watch: {
    selectedCurrency(newVal) {
      if (this.isInitializingSelection) return;
      if (!newVal) return;
      this.dlog("selectedCurrency changed ->", newVal);

      // Use the actual product currency from the prop, not assumptions based on price
      const actualProductCurrency = this.productCurrency.code;

      // Find the correct exchange rate to use for conversion
      let exchangeRateToUse;
      let rateSource;

      if (actualProductCurrency === "LAK" && newVal.code === "THB") {
        // Converting from LAK to THB: use THB's exchange rate
        exchangeRateToUse = Number(
          newVal.store_exchange_rate || newVal.exchange_rate || 1
        );
        rateSource = "THB rate";
      } else if (actualProductCurrency === "THB" && newVal.code === "LAK") {
        // Converting from THB to LAK: use THB's exchange rate (find THB currency from list)
        const thbCurrency = this.currencies.find((c) => c.code === "THB");
        exchangeRateToUse = Number(
          thbCurrency?.store_exchange_rate || thbCurrency?.exchange_rate || 1
        );
        rateSource = "THB rate from currencies list";
      } else {
        // Same currency or fallback
        exchangeRateToUse = Number(
          newVal.store_exchange_rate || newVal.exchange_rate || 1
        );
        rateSource = "selected currency rate";
      }

      // Determine conversion direction based on product currency vs selected currency
      let convertedAmount;
      let formula;

      if (actualProductCurrency === newVal.code) {
        // Same currency, no conversion needed
        convertedAmount = this.totalPrice;
        formula = `${this.totalPrice} (same currency)`;
      } else if (actualProductCurrency === "LAK" && newVal.code === "THB") {
        // Converting from LAK to THB: divide by THB rate
        convertedAmount =
          exchangeRateToUse > 0
            ? this.totalPrice / exchangeRateToUse
            : this.totalPrice;
        formula = `${this.totalPrice} / ${exchangeRateToUse} = ${convertedAmount}`;
      } else if (actualProductCurrency === "THB" && newVal.code === "LAK") {
        // Converting from THB to LAK: multiply by THB rate
        convertedAmount = this.totalPrice * exchangeRateToUse;
        formula = `${this.totalPrice} * ${exchangeRateToUse} = ${convertedAmount}`;
      } else {
        // Fallback: assume totalPrice is in LAK (old behavior)
        convertedAmount =
          exchangeRateToUse > 0
            ? this.totalPrice / exchangeRateToUse
            : this.totalPrice;
        formula = `${this.totalPrice} / ${exchangeRateToUse} = ${convertedAmount} (fallback)`;
      }

      this.displayExchangeRate = exchangeRateToUse.toFixed(2).toString();
      this.inputBuffer = convertedAmount.toFixed(2).toString();
      this.dlog("conversion", {
        totalPrice: this.totalPrice,
        productCurrency: actualProductCurrency,
        selected: newVal.code,
        exchangeRateToUse,
        result: this.inputBuffer,
      });
      // Bubble currency change upward immediately so payment pad can update
      this.$emit("currency-change", newVal);
    },
  },

  computed: {
    colorPad() {
      try {
        return localStorage.getItem("darkMode") === "true" ? "#333" : "#f0f0f0";
      } catch (error) {
        console.error("Error getting color pad:", error);
        return "#f0f0f0";
      }
    },

    keys() {
      try {
        const result = this.generateKeys();
        return result;
      } catch (error) {
        console.error("Error in keys computed property:", error);
        return [];
      }
    },
  },
  methods: {
    dlog(label, payload) {
      if (this.debug) {
        try {
          console.debug(`[NumericExchangeRate] ${label}`, payload);
        } catch (_) {}
      }
    },
    updateDisplayFromRate() {
      if (!this.selectedCurrency) {
        this.displayExchangeRate = "0";
        this.inputBuffer = "0";
        return;
      }

      // Use the actual product currency from the prop, not assumptions based on price
      const actualProductCurrency = this.productCurrency.code;

      // Find the correct exchange rate to use for conversion
      let exchangeRateToUse;
      let rateSource;

      if (
        actualProductCurrency === "LAK" &&
        this.selectedCurrency.code === "THB"
      ) {
        // Converting from LAK to THB: use THB's exchange rate
        exchangeRateToUse = Number(
          this.selectedCurrency.store_exchange_rate ||
            this.selectedCurrency.exchange_rate ||
            1
        );
        rateSource = "THB rate";
      } else if (
        actualProductCurrency === "THB" &&
        this.selectedCurrency.code === "LAK"
      ) {
        // Converting from THB to LAK: use THB's exchange rate (find THB currency from list)
        const thbCurrency = this.currencies.find((c) => c.code === "THB");
        exchangeRateToUse = Number(
          thbCurrency?.store_exchange_rate || thbCurrency?.exchange_rate || 1
        );
        rateSource = "THB rate from currencies list";
      } else {
        // Same currency or fallback
        exchangeRateToUse = Number(
          this.selectedCurrency.store_exchange_rate ||
            this.selectedCurrency.exchange_rate ||
            1
        );
        rateSource = "selected currency rate";
      }

      // Determine conversion direction based on product currency vs selected currency
      let convertedAmount;
      let formula;

      if (actualProductCurrency === this.selectedCurrency.code) {
        // Same currency, no conversion needed
        convertedAmount = this.totalPrice;
        formula = `${this.totalPrice} (same currency)`;
      } else if (
        actualProductCurrency === "LAK" &&
        this.selectedCurrency.code === "THB"
      ) {
        // Converting from LAK to THB: divide by THB rate
        convertedAmount =
          exchangeRateToUse > 0
            ? this.totalPrice / exchangeRateToUse
            : this.totalPrice;
        formula = `${this.totalPrice} / ${exchangeRateToUse} = ${convertedAmount}`;
      } else if (
        actualProductCurrency === "THB" &&
        this.selectedCurrency.code === "LAK"
      ) {
        // Converting from THB to LAK: multiply by THB rate
        convertedAmount = this.totalPrice * exchangeRateToUse;
        formula = `${this.totalPrice} * ${exchangeRateToUse} = ${convertedAmount}`;
      } else {
        // Fallback: assume totalPrice is in LAK (old behavior)
        convertedAmount =
          exchangeRateToUse > 0
            ? this.totalPrice / exchangeRateToUse
            : this.totalPrice;
        formula = `${this.totalPrice} / ${exchangeRateToUse} = ${convertedAmount} (fallback)`;
      }

      const value = Number(convertedAmount || 0)
        .toFixed(2)
        .toString();

      this.displayExchangeRate = exchangeRateToUse.toFixed(2).toString();
      this.inputBuffer = value;
    },
    generateKeys() {
      const baseKeys = [
        [
          { id: 1, label: "7" },
          { id: 2, label: "8" },
          { id: 3, label: "9" },
        ],
        [
          { id: 4, label: "4" },
          { id: 5, label: "5" },
          { id: 6, label: "6" },
        ],
        [
          { id: 7, label: "1" },
          { id: 8, label: "2" },
          { id: 9, label: "3" },
        ],
        [
          { id: 10, label: "." },
          { id: 11, label: "0" },
          { id: 12, icon: "fal fa-backspace", action: this.backspace },
        ],
      ];

      // Ensure all keys have valid id properties
      const finalResult = baseKeys.map((row) =>
        row.map((key) => ({
          ...key,
          id: key.id || Math.random().toString(36).substr(2, 9),
        }))
      );

      return finalResult;
    },

    appendToInput(value) {
      try {
        if (!value || typeof value !== "string") return;

        let current = String(this.inputBuffer || "0");

        // Handle dot
        if (value === ".") {
          if (current.includes(".")) return; // only one dot
          this.inputBuffer = current === "0" ? "0." : current + ".";
          return;
        }

        // Digits only
        if (!/^[0-9]$/.test(value)) return;

        if (current === "0") {
          this.inputBuffer = value;
        } else {
          this.inputBuffer = current + value;
        }
      } catch (error) {
        console.error("Error appending to input:", error);
      }
    },
    backspace() {
      try {
        const current = String(this.inputBuffer || "0");
        if (current.length <= 1) {
          this.inputBuffer = "0";
          return;
        }
        let next = current.slice(0, -1);
        if (next.endsWith(".")) {
          next = next.slice(0, -1);
        }
        this.inputBuffer = next.length ? next : "0";
      } catch (error) {
        console.error("Error in backspace:", error);
        this.inputBuffer = "0";
      }
    },
    clear() {
      try {
        this.inputBuffer = "0";
      } catch (error) {
        console.error("Error in clear method:", error);
      }
    },
    full() {
      try {
        // Use the converted amount instead of the original totalPrice
        const convertedAmount = this.inputBuffer || 0;
        this.inputBuffer = Number(convertedAmount).toFixed(2).toString();
      } catch (error) {
        console.error("Error in full method:", error);
        this.inputBuffer = "0";
      }
    },
    submit() {
      try {
        this.selectedCurrency.input_buffer = this.inputBuffer;
        this.defineStore.setCurrency(this.selectedCurrency);
        const amtNumber = Number(this.inputBuffer || 0);
        this.dlog("submit ->", {
          currency: this.selectedCurrency,
          buffer: this.inputBuffer,
          amountNumber: amtNumber,
        });
        // Emit currency as well for parent to synchronize quickly
        this.$emit("currency-change", this.selectedCurrency);
        this.$emit("submit", amtNumber);
      } catch (error) {
        console.error(
          "❌ NumericExchangeRate - Error in submit method:",
          error
        );
      }
    },
    handleKeydown(event) {
      // Skip if user is typing in another input field
      const activeEl = document.activeElement;
      const tag = activeEl?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        activeEl?.isContentEditable
      ) {
        return;
      }

      if (!event || !event.key) return;
      // if backspace key is pressed
      switch (event.key) {
        case "1":
          event.preventDefault();
          this.appendToInput("1");
          break;
        case "2":
          event.preventDefault();
          this.appendToInput("2");
          break;
        case "3":
          event.preventDefault();
          this.appendToInput("3");
          break;
        case "4":
          event.preventDefault();
          this.appendToInput("4");
          break;
        case "5":
          event.preventDefault();
          this.appendToInput("5");
          break;
        case "6":
          event.preventDefault();
          this.appendToInput("6");
          break;
        case "7":
          event.preventDefault();
          this.appendToInput("7");
          break;
        case "8":
          event.preventDefault();
          this.appendToInput("8");
          break;
        case "9":
          event.preventDefault();
          this.appendToInput("9");
          break;
        case "0":
          event.preventDefault();
          this.appendToInput("0");
          break;
        case ".":
          event.preventDefault();
          this.appendToInput(".");
          break;
        case "Backspace":
          event.preventDefault();
          this.backspace();
          break;
      }
    },
  },
};
</script>

<style scoped>
.numeric-keyboard .v-btn {
  height: 78px;
  width: 100%;
  font-weight: 400;
  font-size: 1rem;
  box-shadow: none;
  border-right: 0.1px solid #ccc;
  border-bottom: 0.1px solid #ccc;
}

.numeric-keyboard {
  border-top: 0.1px solid #ccc;
  border-left: 0.1px solid #ccc;
}
.numeric-keyboard .row .col button.submit-btn {
  background-color: #5b39c9;
  color: #fff;
  border: none;
  box-shadow: none;
}
.numeric-keyboard .v-btn.theme--dark:hover {
  background-color: #333;
}

.numeric-keyboard .v-btn.theme--light:hover {
  background-color: #e6e4e4;
}
</style>
